import type { ReactNode } from 'react'
import { AlertTriangle, Check, Pause, Play, RotateCcw, StepBack, StepForward, X } from 'lucide-react'
import { cn } from '@/lib/utils'
// Relative import (not the '@/components' barrel) to avoid a cycle now that the barrel exports
// Workbench, which renders this file.
import { Orb } from '../Orb/Orb'
import { Slider } from '../Slider/Slider'
import {
  BAR_SPEED_STEP,
  barSpeedLabel,
  clampBarMs,
  FASTEST_BAR_MS,
  isRealTime,
  msForPosition,
  positionForMs,
} from './walkthroughPacing'

export interface WorkbenchReplayBarProps {
  playing: boolean
  /**
   * The run has reached its end. The play seat offers Replay instead of Play, the state line
   * says complete, and the count stops naming a target it has met.
   */
  atEnd: boolean
  /** Play the walkthrough again from the top. */
  onReplay: () => void
  /** Trades whose entry the replay has revealed so far, and the run's total on this chart. */
  tradesRevealed: number
  tradeTotal: number
  /** Where the replay starts, as a date the state line can print. */
  fromLabel: string
  /**
   * Wall-clock milliseconds per bar — the transport's one pacing value, the same on every
   * timeframe. See walkthroughPacing for the range and what each end of it means.
   */
  barMs: number
  onBarMsChange: (ms: number) => void
  /** Index space, from useScrubProjection. */
  value: number
  max: number
  /** Cap so the user can never aim past data the run has actually produced. */
  maxScrubIndex?: number
  onScrub: (index: number) => void
  onTogglePlay: () => void
  onStepBack: () => void
  onStepForward: () => void
  /**
   * Draw the step buttons. Off by default: the band carries the one control anybody reaches
   * for, play/pause, and the steppers are HIDDEN rather than removed — the engine calls behind
   * them still exist and a later part may want them back.
   */
  showSteps?: boolean
  className?: string
}

/** The band's frame: one rule facing the chart, in the band's own colour. */
const BAND =
  'relative w-full shrink-0 bg-card select-none border-t border-accent-walkthrough/40 px-3'

/**
 * A band control: a bare glyph, not a plate.
 *
 * A boxed 28px surface either pushed the row or bled outside the band's own padding on a
 * phone. What separates the control from the captions beside it is INK, not a box — it carries
 * `text-foreground` where the state's terms and the speed's end words stay muted. The tap target
 * is grown with a pseudo-element, more so on a phone, so the glyph keeps its exact seat.
 *
 * `title` is a hover affordance and never fires on touch, so the control also carries its name
 * for assistive tech.
 */
function Seat({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string
  onClick: () => void
  disabled?: boolean
  children: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      disabled={disabled}
      className="relative flex shrink-0 items-center justify-center text-foreground hover:text-foreground/80
                 transition-colors cursor-pointer rounded-sm outline-none focus-visible:ring-1 focus-visible:ring-ring
                 disabled:opacity-40 disabled:pointer-events-none
                 after:absolute after:content-[''] after:-inset-1 max-md:after:-inset-3
                 w-5 h-5 2xl:w-3.5 2xl:h-3.5"
    >
      {children}
    </button>
  )
}

/**
 * The walkthrough's transport: ONE band, docked at the foot of the chart column.
 *
 * It used to have two shapes — a floating, draggable pill over the chart on desktop, a fixed
 * row under it on a phone. Now it is the row everywhere: a sibling of the chart box, so it
 * spans the chart's own width, rests flush on the trades pane, and never covers a candle. The
 * chart flexes down by exactly the band's height, so its time axis stays fully visible. Its
 * separating edge is a top rule, in the band's own colour, facing the chart it reports on.
 *
 * THREE JOBS, ONE ROW FROM 1024px UP, three stacked rows below it:
 *
 *   STATE                     |  SPEED                |  PLAYBACK
 *   dot · label · terms       |  Real-time … Fast     |  play · progress · %
 *
 * The layout is CSS only — no breakpoint hook — so the same markup serves every width and the
 * two shapes cannot drift apart. Stacked, the rows read state, playback, speed (`order`); on
 * the wide band they are the grid's three columns, every child pinned to row 1 so
 * auto-placement can never push one onto a second line.
 *
 * Kept separate from ChartReplayBar rather than made a variant of it: that is the Backtest
 * tab's bar, with its own hover-scrub track and two existing consumers. What they DO share is
 * the index↔time projection (useScrubProjection).
 *
 * There is no show/hide here: the toolbar's Walkthrough toggle owns that, and hidden, the band
 * goes with the rest of the run.
 */
export function WorkbenchReplayBar({
  playing,
  atEnd,
  onReplay,
  tradesRevealed,
  tradeTotal,
  fromLabel,
  barMs,
  onBarMsChange,
  value,
  max,
  maxScrubIndex,
  onScrub,
  onTogglePlay,
  onStepBack,
  onStepForward,
  showSteps = false,
  className,
}: WorkbenchReplayBarProps) {
  const scrubMax = Math.max(0, max)
  const pct = scrubMax > 0 ? Math.min(100, Math.max(0, (value / scrubMax) * 100)) : 0
  const pctLabel = `${Math.round(pct)}%`

  const scrubTo = (raw: number) => {
    onScrub(maxScrubIndex == null ? raw : Math.min(raw, maxScrubIndex))
  }

  // ── The state line ──────────────────────────────────────────────────────────────────────
  // The band's first job: WHERE the run is, for the trader who does not read the chat. One
  // sentence — the state word in the band's ink, its terms muted after it — so it reads as one
  // fact with its terms attached rather than as competing labels. Once the run has ended there
  // is no target left to count towards: the number of trades it placed IS the whole fact.
  const countText =
    tradeTotal > 0
      ? atEnd
        ? `${tradesRevealed} ${tradesRevealed === 1 ? 'trade' : 'trades'}`
        : `${tradesRevealed} of ${tradeTotal} trades`
      : null

  // ── The speed control ───────────────────────────────────────────────────────────────────
  // ONE SLIDER, NO NUMBERS ANYWHERE: the ends are named, not measured, and there is no readout
  // while the knob is held either — the chart itself is the feedback. Real-time on the left,
  // Fast on the right, and the run's pace follows the knob live — no stops, no snapping. The
  // value it moves is milliseconds per bar (walkthroughPacing), the same on every timeframe;
  // the slider itself works in a 0..1 position over that range's log scale. The dwell is still
  // SPOKEN, for assistive tech, as the slider's value text.
  const ms = clampBarMs(barMs)
  const position = positionForMs(ms)
  // Read through the same clamp the slider can reach, so "at the end" is exactly the value the
  // knob lands on — no float comparison against 0 and 1.
  const atSlowest = isRealTime(ms)
  const atFastest = ms === FASTEST_BAR_MS
  const valueText = barSpeedLabel(ms)

  // ── The play seat ───────────────────────────────────────────────────────────────────────
  // The one control anybody reaches for, on the bar's own line. At the end of the run the same
  // seat wears the replay glyph: there is nothing left to resume, only to watch again, and a
  // second Replay button elsewhere would carry the same offer twice.
  const PlayGlyph = atEnd ? RotateCcw : playing ? Pause : Play
  const playLabel = atEnd ? 'Play the walkthrough again' : playing ? 'Pause' : 'Play'

  // Stepping is a paused-only action, and that is the whole rule.
  const stepDisabled = playing

  return (
    <div
      data-testid="replay-bar"
      className={cn(
        BAND,
        // BREATHING ROOM IN THE STACKED BAND: three rows at a tight gap read as one dense block,
        // so the rows get 12px between them and the band's own padding steps up with them —
        // 16px above the first row's ink and 20px below the last, which pays back the extra
        // height the glyph seats give row 2. The wide band is one line and keeps 12px.
        'flex flex-col gap-3 pt-4 pb-5',
        // The state column may give way (its terms truncate) and the progress track may not
        // vanish: the chart column is often far narrower than the viewport this breakpoint
        // measures — with the chat open it can be ~700px wide at a 1280px window.
        '2xl:grid 2xl:grid-cols-[minmax(0,auto)_auto_minmax(10rem,1fr)] 2xl:items-center 2xl:gap-x-8 2xl:gap-y-2 2xl:py-3',
        className,
      )}
    >
      {/* COLUMN 1 — the state. Stacked: the terms go hard right, the label left. Wide: one
          sentence, hugging its own words, so the track beside it takes whatever is left. */}
      <div
        className="order-1 2xl:order-1 2xl:col-start-1 2xl:row-start-1 flex items-center gap-2 min-w-0
                   2xl:gap-1.5 2xl:whitespace-nowrap"
      >
        {/* ONE GLYPH COLUMN: every stacked row seats its glyph in this 20px cell with the row's
            8px gap after it, so the three labels start on one x. */}
        <span className="flex w-5 shrink-0 items-center justify-center">
          {atEnd ? (
            // A STILL CHECK, NOT A TICK IN A COLOUR: this says the run reached its end, not that
            // the strategy passed anything.
            <Check size={12} strokeWidth={2} className="text-muted-foreground" />
          ) : (
            // The live cue: the app's thinking orb, in the band's own colour rather than the
            // house ink, because this surface's cue owns a colour of its own. Decorative — the
            // state word beside it says what the orb shows.
            <Orb state="walkthrough" size={20} token="--accent-walkthrough" aria-hidden />
          )}
        </span>
        <span className={cn('text-caption shrink-0', atEnd ? 'text-muted-foreground' : 'text-foreground')}>
          {atEnd ? 'Walkthrough complete' : 'Walkthrough running'}
        </span>
        <span aria-hidden className="hidden 2xl:inline text-caption text-muted-foreground shrink-0">·</span>
        <span className="text-caption text-muted-foreground tabular-nums truncate ml-auto 2xl:ml-0">
          {/* BELOW 640 THE DATE GOES: the phone row has room for the state word and ONE term, and
              the count is the one that changes while you watch. The longest segment is also the
              one a trader only needs to read once. */}
          <span className="hidden md:inline">{`From ${fromLabel}${countText ? ' · ' : ''}`}</span>
          {countText}
        </span>
      </div>

      {/* COLUMN 2 — the speed. Last when stacked, so the playback row sits under the state. */}
      <div
        className="order-3 2xl:order-2 2xl:col-start-2 2xl:row-start-1 flex items-center gap-2 w-full min-w-0
                   2xl:w-56 2xl:shrink-0"
      >
        {/* THE END WORD LIGHTS WHEN THE KNOB IS ON IT. With no numbers on the track, at the
            extremes the name of the end is the only confirmation that the knob has landed — and
            only at the extreme, never in between. */}
        <span
          className={cn(
            'text-caption shrink-0 transition-colors duration-150',
            atSlowest ? 'text-foreground' : 'text-muted-foreground',
          )}
        >
          Real-time
        </span>
        <Slider
          aria-label="Bar speed"
          aria-valuetext={valueText}
          min={0}
          max={1}
          step={BAR_SPEED_STEP}
          value={[position]}
          onValueChange={([v]) => onBarMsChange(msForPosition(v))}
          // On a phone the root is a 36px row, so a press anywhere on it seeks — Radix moves the
          // knob to any press on the root — which is the thumb target the 12px knob alone is not.
          className="flex-1 min-w-0 max-md:h-9 [&_[data-slot=slider-track]]:h-0.5"
        />
        <span
          className={cn(
            'text-caption shrink-0 transition-colors duration-150',
            atFastest ? 'text-foreground' : 'text-muted-foreground',
          )}
        >
          Fast
        </span>
      </div>

      {/* COLUMN 3 — the playback: its one control, the bar it drives, and how far through. */}
      <div className="order-2 2xl:order-3 2xl:col-start-3 2xl:row-start-1 flex items-center gap-2 w-full min-w-0">
        {showSteps && (
          <Seat label="Step back one bar" onClick={onStepBack} disabled={stepDisabled}>
            <StepBack size={14} strokeWidth={1.75} />
          </Seat>
        )}
        <Seat label={playLabel} onClick={atEnd ? onReplay : onTogglePlay}>
          <PlayGlyph size={14} strokeWidth={1.75} />
        </Seat>
        {showSteps && (
          <Seat label="Step forward one bar" onClick={onStepForward} disabled={stepDisabled}>
            <StepForward size={14} strokeWidth={1.75} />
          </Seat>
        )}

        {/* THE PROGRESS BAR IS A SEEK DRESSED AS A PLAY BAR. The knob is hidden at rest and
            appears on hover of the track — on a POINTER device. Tailwind wraps hover in
            `@media (hover: hover)`, so a touch screen gets the knob always, with the whole
            44px-tall root as its target: Radix seeks on any press on the root, which a
            pseudo-element on the 12px knob could never widen. Keyboard focus shows it too —
            `focus-visible`, not `focus-within`: Radix leaves the thumb focused after a mouse
            drag, and the knob meant to appear on hover would otherwise stay until the next
            click.

            The gesture is Radix's, not a hand-rolled drag. The old phone band handed its
            scrub to a native range because iOS Safari cancels a hand-rolled horizontal drag
            the moment it claims it for the page; Radix sets `touch-action: none` on the root
            BEFORE any touch begins, which is the one arrangement Safari honours. */}
        <span className="group/scrub flex flex-1 min-w-0 items-center">
          <Slider
            aria-label="Run progress"
            aria-valuetext={pctLabel}
            min={0}
            max={scrubMax}
            step={1}
            value={[Math.min(value, scrubMax)]}
            disabled={scrubMax === 0}
            onValueChange={([v]) => scrubTo(v)}
            className={cn(
              'flex-1 min-w-0 max-md:h-11 [&_[data-slot=slider-track]]:h-1',
              '[&_[data-slot=slider-range]]:bg-accent-walkthrough/70',
              // THE KNOB IS THE BAND'S COLOUR TOO: a foreground knob on an amber track read as
              // a different control sitting on the run's own bar.
              '[&_[data-slot=slider-thumb]]:bg-accent-walkthrough [&_[data-slot=slider-thumb]]:border-accent-walkthrough',
              '[&_[data-slot=slider-thumb]]:hover:ring-accent-walkthrough/20',
              '[&_[data-slot=slider-thumb]]:opacity-0 [&_[data-slot=slider-thumb]]:transition-opacity',
              'group-hover/scrub:[&_[data-slot=slider-thumb]]:opacity-100',
              '[&_[data-slot=slider-thumb]]:focus-visible:opacity-100',
              'max-md:[&_[data-slot=slider-thumb]]:opacity-100',
              'pointer-coarse:[&_[data-slot=slider-thumb]]:opacity-100',
            )}
          />
        </span>
        {/* THE PERCENT HUGS THE BAR. Stacked it sits in a fixed cell so the progress track and
            the speed track beneath it end on one vertical; the wide band sizes it to its word. */}
        <span className="text-caption text-muted-foreground/70 tabular-nums text-right shrink-0 w-9 2xl:w-auto">
          {pctLabel}
        </span>
      </div>
    </div>
  )
}

export interface WorkbenchReplayStatusProps {
  /** Preparing a run, or telling why one did not start. */
  status: 'preparing' | 'error'
  /** Preparing: a first run, or one replacing the run on screen. */
  reason?: 'start' | 'rebuild'
  /** Preparing: null while the settings are worked out, 0 queued, 1 running. */
  runStatus?: number | null
  /** Preparing: how far the engine has got, 0..100 (only shown once it is running). */
  progressPct?: number
  /** Preparing for long enough that the copy should say it is still going. */
  slow?: boolean
  errorMessage?: string
  errorAction?: 'billing'
  onRetry?: () => void
  onManageBilling?: () => void
  /** Error: put the band back to what it was (the previous run, or nothing). */
  onDismiss?: () => void
  className?: string
}

/**
 * The band while a walkthrough is being prepared, or when one did not start.
 *
 * It used to be a card over the chart. Now the chart is where the user keeps working while the
 * run is built — drawing, scrolling, reading the last run's trades — so the wait lives in the
 * band, in one line, and covers nothing. The user chose not to cap the drawn span, so a wide
 * run can take minutes: a real percentage (from the engine's own progress) is what tells
 * "working" from "stuck".
 */
export function WorkbenchReplayStatus({
  status,
  reason = 'start',
  runStatus = null,
  progressPct = 0,
  slow = false,
  errorMessage,
  errorAction,
  onRetry,
  onManageBilling,
  onDismiss,
  className,
}: WorkbenchReplayStatusProps) {
  if (status === 'error') {
    return (
      <div
        data-testid="replay-bar"
        role="alert"
        className={cn(BAND, 'flex items-center gap-2 py-2.5', className)}
      >
        <span className="flex w-5 shrink-0 items-center justify-center text-danger">
          <AlertTriangle size={13} strokeWidth={1.75} />
        </span>
        <span className="text-caption text-foreground shrink-0">Walkthrough didn&apos;t start</span>
        <span className="text-caption text-muted-foreground truncate min-w-0">{errorMessage}</span>
        <span className="ml-auto flex items-center gap-3 shrink-0">
          {errorAction === 'billing' && onManageBilling ? (
            <button type="button" onClick={onManageBilling} className="text-caption font-medium text-foreground hover:underline underline-offset-2 cursor-pointer">
              Manage billing
            </button>
          ) : (
            onRetry && (
              <button type="button" onClick={onRetry} className="text-caption font-medium text-foreground hover:underline underline-offset-2 cursor-pointer">
                Try again
              </button>
            )
          )}
          {onDismiss && (
            <Seat label="Dismiss" onClick={onDismiss}>
              <X size={14} strokeWidth={1.75} />
            </Seat>
          )}
        </span>
      </div>
    )
  }

  const detail =
    runStatus === null
      ? 'Working out the settings for the period you drew on'
      : runStatus === 0
        ? 'Queued — waiting for a run slot'
        : 'Replaying your strategy over the period you drew on'
  return (
    <div
      data-testid="replay-bar"
      role="status"
      aria-live="polite"
      className={cn(BAND, 'flex items-center gap-2 py-2.5', className)}
    >
      <span className="flex w-5 shrink-0 items-center justify-center">
        <Orb state="walkthrough" size={20} token="--accent-walkthrough" aria-hidden />
      </span>
      <span className="text-caption text-foreground shrink-0">
        {reason === 'rebuild' ? 'Preparing a new walkthrough…' : 'Preparing walkthrough…'}
      </span>
      <span className="text-caption text-muted-foreground truncate min-w-0">
        <span className="hidden md:inline">{slow ? 'Still going — a wide drawing span takes longer' : detail}</span>
      </span>
      {/* Only meaningful once the engine has started reporting; before that 0% reads as stalled. */}
      {runStatus === 1 && (
        <span className="ml-auto text-caption text-muted-foreground/70 tabular-nums shrink-0">{`${progressPct}%`}</span>
      )}
    </div>
  )
}
